import {
    faBarcode,
    faBox,
    faCalendarAlt,
    faCheckCircle,
    faChevronDown,
    faChevronUp,
    faCircle,
    faCoins,
    faMoneyBillWave,
    faSync,
    faTag,
    faUser,
    faWarning
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';

/**
 * Componente para mostrar un producto individual como tarjeta
 */
const ProductoCard = ({ producto, isSelected = false, onToggleSelection }) => {
    const url = `/productos/Detalle/${producto.idProducto}`;
    const esRenovacion = !!producto.bIsPeriodicidad;
    const esServicio = producto.bServicio;
    const sinExistencia = !esServicio && producto.dExistencia === 0;

    const [preciosExpandidos, setPreciosExpandidos] = useState(false);

    const togglePrecios = (e) => {
        e.stopPropagation();
        setPreciosExpandidos(prev => !prev);
    };

    // Función para formatear la fecha de creación
    const formatearFechaCreacion = (fechaString) => {
        if (!fechaString) return null;
        try {
            const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
            const [fechaParte] = fechaString.split("T");
            const [año, mes, dia] = fechaParte.split("-");
            return `${parseInt(dia)} ${meses[parseInt(mes) - 1]} ${año}`;
        } catch {
            return null;
        }
    };

    const fechaCreacion = formatearFechaCreacion(producto.dCreatedDate);

    // Formateo de moneda simple y cálculo de base/IVA para renovaciones
    // const formatCurrency = (value) => {
    //     const num = Number(value) || 0;
    //     return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // };

    // const totalPrecio = Number(producto.dTotal ?? producto.dPrecioUnitario ?? 0);
    // const tasaIVA = Number(producto.dTasaCuotaIVA ?? 0);
    // let precioBase = totalPrecio;
    // let montoIVA = 0;
    // if (producto.vTipoFactorIVA === 'Tasa' && tasaIVA > 0) {
    //     precioBase = totalPrecio / (1 + tasaIVA);
    //     montoIVA = totalPrecio - precioBase;
    // }

    const handleCardClick = (e) => {
        // No activar selección si se hace clic en un botón o enlace
        if (e.target.tagName === 'A' || e.target.closest('a') || e.target.closest('button')) {
            return;
        }

        if (onToggleSelection) {
            onToggleSelection(producto.idProducto);
        }
    };

    // Determinar clases CSS según estado
    const cardClasses = [
        'mb-3',
        'shadow-sm',
        !producto.bIsActive ? 'border-danger border-start border-3' : '',
        sinExistencia ? 'border-warning border-start border-3' : '',
        isSelected ? 'border-primary border-3' : 'border'
    ].filter(Boolean).join(' ');

    // Estilos dinámicos para el fondo
    const cardStyle = {
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderLeft: isSelected ? '4px solid #0d6efd' : undefined,
        ...(isSelected && {
            backgroundColor: '#f8f9fa'
        })
    };

    return (
        <Card
            className={cardClasses}
            onClick={handleCardClick}
            style={cardStyle}
        >
            <Card.Body className="py-3">
                <Row className="align-items-center">
                    {/* ID y Estado */}
                    <Col xs={12} md={2} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column align-items-start">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-primary"
                                        size="lg"
                                    />
                                )}
                                <Link to={url} className="text-decoration-none">
                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#2c7be5' }}>
                                        #{producto.idProducto}
                                    </span>
                                </Link>
                            </div>

                            {/* Imagen del producto */}
                            {producto.vImagenProducto_Base64 && (
                                <div className="mb-2 text-center w-100">
                                    <img
                                        src={`data:image/jpeg;base64,${producto.vImagenProducto_Base64}`}
                                        alt={producto.vNombreProducto}
                                        style={{
                                            width: '100%',
                                            maxWidth: '120px',
                                            height: 'auto',
                                            maxHeight: '120px',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            border: '2px solid #e0e0e0'
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            {/* Estado Activo/Inactivo */}
                            <div className="mb-2">
                                <span className={`fw-bold ${producto.bIsActive ? 'text-success' : 'text-danger'}`}>
                                    <FontAwesomeIcon
                                        icon={faCircle}
                                        style={{ fontSize: '0.6rem' }}
                                        className="me-1"
                                    />
                                    {producto.bIsActive ? 'ACTIVO' : 'INACTIVO'}
                                </span>
                            </div>



                            {/* Advertencia sin stock */}
                            {sinExistencia && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Producto sin existencia</Tooltip>}
                                >
                                    <div className="text-warning fw-bold">
                                        <FontAwesomeIcon icon={faWarning} className="me-1" />
                                        SIN STOCK
                                    </div>
                                </OverlayTrigger>
                            )}

                            {/* Información de creación */}
                            {(fechaCreacion || producto.vCreatedUser) && (
                                <div className="mt-2 w-100">
                                    {fechaCreacion && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Fecha de creación</Tooltip>}
                                        >
                                            <div className="small text-secondary mb-1">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                                <span className="fw-semibold">{fechaCreacion}</span>
                                            </div>
                                        </OverlayTrigger>
                                    )}
                                    {producto.vCreatedUser && (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Creado por</Tooltip>}
                                        >
                                            <div className="small text-secondary">
                                                <FontAwesomeIcon icon={faUser} className="me-1" />
                                                <span className="fw-semibold">{producto.vCreatedUser}</span>
                                            </div>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Información principal del producto */}
                    <Col xs={12} md={7} className="mb-2 mb-md-0">
                        <Row>
                            {/* Columna 1: Datos Básicos */}
                            <Col xs={12} lg={6} className="mb-2 mb-lg-0">
                                <div className="mb-2">
                                    <h6 className="mb-2">
                                        <Link to={url} className="text-decoration-none">
                                            <FontAwesomeIcon icon={faBox} className="me-2 text-primary" />
                                            <span className="text-dark fw-bold" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {producto.vNombreProducto}
                                            </span>
                                        </Link>
                                    </h6>

                                    <div className="small mb-2">
                                        <span className="text-primary fw-bold">INFORMACIÓN DEL PRODUCTO</span>
                                    </div>

                                    {/* Código de Barras */}
                                    {producto.vCodigoBarras && (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faBarcode} className="me-1 text-info" />
                                            <span className="fw-bold text-dark">Código:</span> <span className="text-dark">{producto.vCodigoBarras}</span>
                                        </div>
                                    )}

                                    {/* Tipo de Producto */}
                                    <div className="small mb-1">
                                        <FontAwesomeIcon icon={faTag} className="me-1 text-success" />
                                        <span className="fw-bold text-dark">Tipo:</span> <span className="text-dark">{producto.vTipoProducto === "ModuloExtra" ? "Módulo Extra" : producto.vTipoProducto}</span>
                                    </div>

                                    {/* Información de Módulo */}
                                    {producto.vTipoProducto === "ModuloExtra" && producto.vClaveModulo && (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faTag} className="me-1 text-secondary" />
                                            <span className="fw-bold text-dark">Clave Módulo:</span> <span className="text-dark">{producto.vClaveModulo}</span>
                                        </div>
                                    )}

                                    {/* Tipo de Licencia */}
                                    {producto.idCatTipoLicencia && (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faTag} className="me-1 text-secondary" />
                                            <span className="fw-bold text-dark">Tipo Licencia:</span> <span className="text-dark">{producto.vDescripcionTipo || producto.idCatTipoLicencia}</span>
                                        </div>
                                    )}

                                    {/* Existencia */}
                                    {esServicio ? (
                                        <div className="small">
                                            <FontAwesomeIcon icon={faCoins} className="me-1 text-primary" />
                                            <span className="fw-bold text-primary">SERVICIO</span>
                                        </div>
                                    ) : (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faBox} className="me-1" />
                                            <span className="fw-bold text-dark">Existencia:</span>{' '}
                                            <span className={
                                                producto.dExistencia > 10 ? 'text-success fw-bold' :
                                                    producto.dExistencia > 0 ? 'text-warning fw-bold' :
                                                        'text-danger fw-bold'
                                            }>
                                                {producto.dExistencia}
                                            </span>
                                        </div>
                                    )}

                                    {/* Tipo de Pago */}
                                    <div className="mb-2 small d-flex align-items-center mt-2">
                                        <div
                                            className={`d-inline-flex align-items-center px-2 py-1 rounded ${esRenovacion
                                                ? 'bg-info bg-opacity-10 border border-info'
                                                : 'bg-primary bg-opacity-10 border border-primary'
                                                }`}
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={esRenovacion ? faSync : faMoneyBillWave}
                                                className={`me-2 ${esRenovacion ? 'text-info' : 'text-primary'}`}
                                                style={{ fontSize: '0.8rem' }}
                                            />
                                            <span className={`fw-bold ${esRenovacion ? 'text-info' : 'text-primary'}`}>
                                                {esRenovacion ? 'RENOVACIÓN' : 'PAGO ÚNICO'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            {/* Columna 2: Datos Fiscales */}
                            <Col xs={12} lg={6}>
                                <div className="mb-2">
                                    <div className="small mb-2">
                                        <span className="text-info fw-bold">DATOS FISCALES</span>
                                    </div>

                                    {/* Clave de Unidad SAT con descripción */}
                                    {producto.vClaveUnidadSat && (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faTag} className="me-1 text-info" />
                                            <span className="fw-bold text-dark">Clave Unidad:</span> <span className="text-dark">{producto.vClaveUnidadSat}
                                                {producto.vDescripcion_ClaveUnidad && (
                                                    producto.vDescripcion_ClaveUnidad
                                                )}</span>
                                        </div>
                                    )}

                                    {/* Código SAT */}
                                    {producto.vCodigoProductoServicioSat && (
                                        <div className="small mb-1">
                                            <FontAwesomeIcon icon={faBarcode} className="me-1 text-info" />
                                            <span className="fw-bold text-dark">Código SAT:</span> <span className="text-dark">{producto.vCodigoProductoServicioSat}</span>
                                        </div>
                                    )}

                                    {/* Tipo Factor IVA */}
                                    <div className="small mb-1">
                                        <span className="fw-bold text-dark">Tipo Factor:</span>{' '}
                                        <span className={
                                            producto.vTipoFactorIVA === 'Tasa' ? 'text-success fw-bold' :
                                                producto.vTipoFactorIVA === 'Exento' ? 'text-warning fw-bold' :
                                                    'text-secondary fw-bold'
                                        }>
                                            {producto.vTipoFactorIVA || 'N/A'}
                                        </span>
                                    </div>

                                    {/* Tasa IVA */}
                                    {producto.vTipoFactorIVA === 'Tasa' && (
                                        <div className="small mb-1">
                                            <span className="fw-bold text-dark">IVA:</span> <span className="text-dark">{(producto.dTasaCuotaIVA * 100).toFixed(0)}%</span>
                                        </div>
                                    )}



                                    {/* Moneda */}
                                    {/* {producto.vMoneda && (
                                        <div className="small mb-1">
                                            <span className="fw-bold text-dark">Moneda:</span> <span className="text-dark">{producto.vMoneda}</span>
                                        </div>
                                    )} */}
                                </div>


                            </Col>
                        </Row>
                    </Col>

                    {/* Información de precios */}
                    <Col xs={12} md={3} className="text-md-end">
                        <div className="mb-3">
                            <div className="small mb-2">
                                <span className="text-success fw-bold">PRECIOS</span>
                            </div>

                            {/* Precio principal: solo para pago único */}
                    {!esRenovacion && producto.dTotal != null ? (
                                <>
                                    {/* Precio base */}
                                    <div className="small text-muted mb-1">
                                        Base: <span className="fw-semibold">${parseFloat(producto.dPrecioUnitario ?? 0).toFixed(2)}</span>
                                    </div>

                                    {/* Total destacado */}
                                    <div className="fw-bold text-success mb-2" style={{ fontSize: '1.35rem', lineHeight: 1.1 }}>
                                        ${parseFloat(producto.dTotal ?? 0).toFixed(2)}
                                    </div>

                                    {/* IVA incluido */}
                                    {producto.vTipoFactorIVA === 'Tasa' && parseFloat(producto.dIVA ?? 0) > 0 && (
                                        <div className="small text-muted mb-2">
                                            IVA incl. ${parseFloat(producto.dIVA ?? 0).toFixed(2)}
                                        </div>
                                    )}
                                </>
                            ) : !esRenovacion && (
                                <div className="text-center py-2 mb-2">
                                    <span className="text-muted small fst-italic">Sin precio configurado</span>
                                </div>
                            )}

                            {/* Badges de monedas: únicas desde el array precios */}
                            {(() => {
                                const listaPrecios = producto.precios || [];
                                const monedas = [...new Set(listaPrecios.map(p => p.vMoneda).filter(Boolean))];
                                return monedas.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-end gap-1 flex-wrap mb-1">
                                        {monedas.map((m) => (
                                            <span key={m} className={`badge border ${
                                                m === 'USD'
                                                    ? 'text-primary border-primary bg-primary bg-opacity-10'
                                                    : 'text-success border-success bg-success bg-opacity-10'
                                            }`}>{m}</span>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* Toggle y panel expandido: solo para renovación */}
                            {esRenovacion && (
                                <>
                                    <button
                                        className="btn btn-link btn-sm p-0 mt-2 text-decoration-none text-secondary"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={togglePrecios}
                                    >
                                        <FontAwesomeIcon icon={preciosExpandidos ? faChevronUp : faChevronDown} className="me-1" />
                                        {preciosExpandidos ? 'Ocultar precios' : 'Ver todos los precios'}
                                    </button>

                                    {preciosExpandidos && (
                                        <div className="mt-2 text-start border-top pt-2">
                                            {producto.precios && producto.precios.length > 0 ? (
                                                producto.precios.map((p, i) => (
                                                    <div
                                                        key={i}
                                                        className="d-flex justify-content-between align-items-center py-1"
                                                        style={{ fontSize: '0.78rem', borderTop: i > 0 ? '1px solid #f0f0f0' : 'none' }}
                                                    >
                                                        <span className="text-muted">
                                                            {p.bIsPeriodicidad
                                                                ? p.vDescripcion || 'Recurrente'
                                                                : 'Pago único'
                                                            }
                                                        </span>
                                                        <div className="text-end">
                                                            <span className="fw-semibold text-success">
                                                                ${parseFloat(p.dTotal ?? 0).toFixed(2)}
                                                            </span>
                                                            <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>{p.vMoneda}</span>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="text-muted small fst-italic">Sin precios registrados</span>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Ver detalles link */}
                        <div className="mt-3">
                            <Link to={url} className="btn btn-sm btn-outline-primary">
                                <img src="/img/ver20.png" alt="Ver" className="me-1" />
                                Ver detalles
                            </Link>
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

ProductoCard.propTypes = {
    producto: PropTypes.shape({
        idProducto: PropTypes.number.isRequired,
        vCodigoBarras: PropTypes.string,
        vNombreProducto: PropTypes.string.isRequired,
        vTipoProducto: PropTypes.string,
        vClaveModulo: PropTypes.string,
        idCatTipoLicencia: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        vDescripcionTipo: PropTypes.string,
        bIsPeriodicidad: PropTypes.bool,
        dPrecioUnitario: PropTypes.number,
        dTasaCuotaIVA: PropTypes.number,
        vTipoFactorIVA: PropTypes.string,
        dIVA: PropTypes.number,
        dTotal: PropTypes.number,
        vMoneda: PropTypes.string,
        dExistencia: PropTypes.number,
        bServicio: PropTypes.bool,
        bIsActive: PropTypes.bool,
        vClaveUnidadSat: PropTypes.string,
        vDescripcion_ClaveUnidad: PropTypes.string,
        vCodigoProductoServicioSat: PropTypes.string,
        vImagenProducto_Base64: PropTypes.string,
        dCreatedDate: PropTypes.string,
        vCreatedUser: PropTypes.string,
        precios: PropTypes.arrayOf(PropTypes.shape({
            bIsPeriodicidad: PropTypes.bool,
            vDescripcion: PropTypes.string,
            dTotal: PropTypes.number,
            vMoneda: PropTypes.string
        }))
    }).isRequired,
    isSelected: PropTypes.bool,
    onToggleSelection: PropTypes.func
};

export default ProductoCard;
